/**
 * blocks.tsx — the GenUI block RENDERERS (bounded context: `genui`).
 *
 * The scratchpad reads as ONE cohesive smart document. `BlockNode` is the recursive
 * unit: it renders any block (a leaf via {@link Body}, or a `group` that OWNS and lays
 * out its children — including nested groups), carries a co-edit toolbar (move / edit /
 * remove), and supports in-place editing of every kind. Because it is recursive, a
 * group's children are edited and reordered with the same affordances as top-level
 * blocks; mutating a child rebuilds its parent group and persists through one update.
 *
 * Two interactions make this more than a read-out: `list` items are checkable by human
 * and assistant alike, and `actions` turns proposals into buttons that dispatch the next
 * turn — interaction by clicking, not typing.
 */
import { useState } from "react";
import type { Workspace } from "../../store/events.ts";
import type { BlockData, LeafBlock, GroupData } from "../../genui/blocks.ts";
import type { ScratchpadBlock, BlockAuthor } from "../../genui/scratchpad.ts";
import { resolveRef } from "../../domain/resolve.ts";
import { navigate } from "../lib/router.tsx";
import { Markdown, InlineMarkdown } from "../lib/markdown.tsx";
import { Diagram } from "../lib/diagram.tsx";
import { AutoTextarea } from "../lib/auto-textarea.tsx";
import { BusinessCanvas } from "../business/canvas.tsx";
import type { BusinessCanvasBlock } from "../../domain/business.ts";

export interface BlockHandlers {
  readonly onRemove: (id: string) => void;
  readonly onMove: (id: string, dir: -1 | 1) => void;
  readonly onUpdate: (id: string, data: BlockData) => void;
  /** A clickable proposal/decision dispatches its `send` text as the next assistant turn. */
  readonly onAction: (send: string) => void;
}

/** Handlers for a single recursive node (top-level or a group child). */
interface NodeHandlers {
  readonly onChange: (data: BlockData) => void;
  readonly onAction: (send: string) => void;
  readonly onRemove?: () => void;
  readonly onMoveUp?: () => void;
  readonly onMoveDown?: () => void;
}

const STATUS_GLYPH: Record<string, string> = { todo: "☐", done: "☑", blocked: "⊘" };
const cycle = (s: "todo" | "done" | "blocked" | undefined): "todo" | "done" | "blocked" =>
  s === "todo" ? "done" : s === "done" ? "blocked" : "todo";

// ── Read view for a LEAF block ──────────────────────────────────────────────────
function Body({ ws, data, onUpdate, onAction, readOnly }: { ws: Workspace; data: LeafBlock; onUpdate: (data: BlockData) => void; onAction: (send: string) => void; readOnly?: boolean | undefined }): JSX.Element {
  switch (data.kind) {
    case "heading": {
      const lvl = data.level ?? 2;
      const cls = `gb-h gb-h${lvl}`;
      return lvl === 1 ? <h2 className={cls}>{data.text}</h2> : lvl === 2 ? <h3 className={cls}>{data.text}</h3> : <h4 className={cls}>{data.text}</h4>;
    }
    case "prose":
      return <Markdown source={data.text} className="gb-prose" />;
    case "callout":
      return (
        <div className={`gb-callout tone-${data.tone}`}>
          <span className="gb-callout-icon" aria-hidden="true">{data.tone === "warn" ? "⚠" : data.tone === "boundary" ? "≠" : data.tone === "insight" ? "✦" : "ℹ"}</span>
          <div>
            {data.title ? <div className="gb-callout-title"><InlineMarkdown source={data.title} /></div> : null}
            <Markdown source={data.body} className="gb-callout-body gb-md" />
          </div>
        </div>
      );
    case "fields":
      return (
        <div className="gb-fields">
          {data.title ? <div className="gb-title">{data.title}</div> : null}
          <dl className="gb-kv">
            {data.rows.map((r, i) => (
              <div className="gb-kv-row" key={i}>
                <dt>{r.label}</dt>
                <dd><Markdown source={r.value} className="gb-md" /></dd>
              </div>
            ))}
          </dl>
        </div>
      );
    case "list":
      return (
        <div className="gb-list">
          {data.title ? <div className="gb-title">{data.title}</div> : null}
          <ul className="gb-items">
            {data.items.map((it, i) => (
              <li key={i} className={it.status ? `st-${it.status}` : ""}>
                {it.status ? (
                  readOnly ? (
                    <span className="gb-marker">{STATUS_GLYPH[it.status]}</span>
                  ) : (
                    <button className="gb-check" title={`${it.status} — click to change`} onClick={() => onUpdate({ ...data, items: data.items.map((x, j) => (j === i ? { ...x, status: cycle(x.status) } : x)) })}>
                      {STATUS_GLYPH[it.status]}
                    </button>
                  )
                ) : (
                  <span className="gb-marker">{data.ordered ? `${i + 1}.` : "▸"}</span>
                )}
                <span className="gb-item-text"><InlineMarkdown source={it.text} /></span>
              </li>
            ))}
          </ul>
        </div>
      );
    case "table":
      return (
        <div className="gb-table">
          {data.title ? <div className="gb-title">{data.title}</div> : null}
          <table className="reg">
            <thead><tr>{data.columns.map((c, i) => <th key={i}><InlineMarkdown source={c} /></th>)}</tr></thead>
            <tbody>
              {data.rows.map((row, i) => (
                <tr key={i}>{row.map((cell, j) => <td key={j}><InlineMarkdown source={cell} /></td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "diagram":
      return (
        <div className="gb-diagram">
          {data.title ? <div className="gb-title">{data.title}</div> : null}
          <Diagram source={data.source} />
        </div>
      );
    case "entities":
      return (
        <div className="gb-entities">
          {data.title ? <div className="gb-title">{data.title}</div> : null}
          <div className="gb-entity-cards">
            {data.ids.map((eid) => {
              const ref = resolveRef(ws, eid);
              return (
                <button key={eid} className={`gb-entity ${ref.route ? "nav" : ""}`} onClick={() => ref.route && navigate(ref.route)} title={eid}>
                  <span className="gb-entity-kind">{ref.kind}</span>
                  <span className="gb-entity-label">{ref.label}</span>
                  {ref.sublabel ? <span className="gb-entity-sub">{ref.sublabel}</span> : null}
                </button>
              );
            })}
          </div>
        </div>
      );
    case "stat":
      return (
        <div className="gb-stats">
          {data.items.map((s, i) => (
            <div className="gb-stat" key={i}>
              <span className="gb-stat-value">{s.value}</span>
              <span className="gb-stat-label">{s.label}</span>
              {s.note ? <span className="gb-stat-note">{s.note}</span> : null}
            </div>
          ))}
        </div>
      );
    case "actions":
      return (
        <div className="gb-actions">
          {data.title ? <div className="gb-title">{data.title}</div> : null}
          {data.prompt ? <div className="gb-actions-prompt">{data.prompt}</div> : null}
          <div className="gb-actions-row">
            {data.options.map((o, i) => (
              <button key={i} className={`gb-action tone-${o.tone ?? "default"}`} disabled={readOnly} onClick={() => !readOnly && onAction(o.send)}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
      );
    case "business-canvas":
      // The typed Business Operating Domain component manages its OWN inline editing,
      // so the read view passes `onUpdate` straight through — no ✎ round-trip needed.
      return <BusinessCanvas block={data} readOnly={readOnly} onChange={readOnly ? undefined : (b) => onUpdate(b)} />;
  }
}

// ── Edit view ─────────────────────────────────────────────────────────────────────
function Lbl({ children }: { children: React.ReactNode }): JSX.Element {
  return <span className="gb-ed-lbl">{children}</span>;
}
function RowBtns({ onAdd, onDel }: { onAdd: () => void; onDel: () => void }): JSX.Element {
  return (
    <span className="gb-ed-rowbtns">
      <button type="button" className="gb-tb" onClick={onDel} aria-label="remove row">✕</button>
      <button type="button" className="gb-tb" onClick={onAdd} aria-label="add row">＋</button>
    </span>
  );
}

/**
 * The one text editor used everywhere a block holds free text. It is an auto-growing
 * textarea (so any field accepts multiline, markdown-formatted values, defaulting to a
 * single line and growing with content) styled — via `gb-inp` — to match the read
 * typography, so edit and read modes look the same apart from the focus underline.
 */
function EdInput({ value, onChange, placeholder, className }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }): JSX.Element {
  return (
    <AutoTextarea
      rows={1}
      className={`gb-inp${className ? ` ${className}` : ""}`}
      value={value}
      {...(placeholder ? { placeholder } : {})}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** The optional `title` field, edited in the read view's own title slot. */
function EdTitle({ value, onChange }: { value: string | undefined; onChange: (v: string) => void }): JSX.Element {
  return (
    <label className="gb-ed-titlefield">
      <span className="gb-ed-lbl">title</span>
      <EdInput value={value ?? ""} placeholder="optional" onChange={onChange} className="gb-inp-title" />
    </label>
  );
}

function BlockEditor({ data, onSave, onCancel }: { data: BlockData; onSave: (d: BlockData) => void; onCancel: () => void }): JSX.Element {
  const [d, setD] = useState<BlockData>(data);

  const editor = ((): JSX.Element => {
    switch (d.kind) {
      case "heading":
        return (
          <div className="gb-ed-row">
            <EdInput value={d.text} placeholder="heading" className={`gb-inp-h gb-inp-h${d.level ?? 2}`} onChange={(v) => setD({ ...d, text: v })} />
            <select className="field-input gb-ed-sm" value={d.level ?? 2} onChange={(e) => setD({ ...d, level: Number(e.target.value) as 1 | 2 | 3 })}>
              <option value={1}>H1</option><option value={2}>H2</option><option value={3}>H3</option>
            </select>
          </div>
        );
      case "prose":
        return <EdInput value={d.text} placeholder="text — markdown supported" className="gb-inp-prose" onChange={(v) => setD({ ...d, text: v })} />;
      case "callout":
        return (
          <div className={`gb-callout tone-${d.tone} gb-callout-edit`}>
            <span className="gb-callout-icon" aria-hidden="true">{d.tone === "warn" ? "⚠" : d.tone === "boundary" ? "≠" : d.tone === "insight" ? "✦" : "ℹ"}</span>
            <div className="gb-callout-edit-body">
              <div className="gb-ed-row">
                <select className="field-input gb-ed-sm" value={d.tone} onChange={(e) => setD({ ...d, tone: e.target.value as typeof d.tone })}>
                  {(["info", "insight", "warn", "boundary"] as const).map((tn) => <option key={tn} value={tn}>{tn}</option>)}
                </select>
                <EdInput value={d.title ?? ""} placeholder="title (optional)" className="gb-callout-title" onChange={(v) => setD({ ...d, title: v })} />
              </div>
              <EdInput value={d.body} placeholder="body — markdown supported" onChange={(v) => setD({ ...d, body: v })} />
            </div>
          </div>
        );
      case "fields":
        return (
          <>
            <EdTitle value={d.title} onChange={(v) => setD({ ...d, title: v })} />
            <dl className="gb-kv gb-kv-edit">
              {d.rows.map((r, i) => (
                <div className="gb-kv-row" key={i}>
                  <EdInput value={r.label} placeholder="label" className="gb-inp-key" onChange={(v) => setD({ ...d, rows: d.rows.map((x, j) => (j === i ? { ...x, label: v } : x)) })} />
                  <div className="gb-kv-val">
                    <EdInput value={r.value} placeholder="value — markdown supported" onChange={(v) => setD({ ...d, rows: d.rows.map((x, j) => (j === i ? { ...x, value: v } : x)) })} />
                    <RowBtns onDel={() => setD({ ...d, rows: d.rows.filter((_, j) => j !== i) })} onAdd={() => setD({ ...d, rows: [...d.rows.slice(0, i + 1), { label: "", value: "" }, ...d.rows.slice(i + 1)] })} />
                  </div>
                </div>
              ))}
            </dl>
          </>
        );
      case "list":
        return (
          <>
            <EdTitle value={d.title} onChange={(v) => setD({ ...d, title: v })} />
            <label className="gb-ed-check"><input type="checkbox" checked={d.ordered ?? false} onChange={(e) => setD({ ...d, ordered: e.target.checked })} /> ordered</label>
            <ul className="gb-items gb-items-edit">
              {d.items.map((it, i) => (
                <li key={i} className={it.status ? `st-${it.status}` : ""}>
                  <select className="gb-ed-marker" title="status" value={it.status ?? ""} onChange={(e) => setD({ ...d, items: d.items.map((x, j) => {
                    if (j !== i) return x;
                    if (e.target.value) return { ...x, status: e.target.value as "todo" | "done" | "blocked" };
                    const { status: _drop, ...rest } = x;
                    return rest;
                  }) })}>
                    <option value="">{d.ordered ? `${i + 1}.` : "▸"}</option><option value="todo">☐ todo</option><option value="done">☑ done</option><option value="blocked">⊘ blocked</option>
                  </select>
                  <EdInput value={it.text} placeholder="item — markdown supported" className="gb-item-text" onChange={(v) => setD({ ...d, items: d.items.map((x, j) => (j === i ? { ...x, text: v } : x)) })} />
                  <RowBtns onDel={() => setD({ ...d, items: d.items.filter((_, j) => j !== i) })} onAdd={() => setD({ ...d, items: [...d.items.slice(0, i + 1), { text: "" }, ...d.items.slice(i + 1)] })} />
                </li>
              ))}
            </ul>
          </>
        );
      case "table":
        return (
          <>
            <EdTitle value={d.title} onChange={(v) => setD({ ...d, title: v })} />
            <Lbl>columns</Lbl>
            <div className="gb-ed-row">
              {d.columns.map((c, i) => (
                <EdInput key={i} value={c} placeholder={`col ${i + 1}`} className="gb-ed-sm" onChange={(v) => setD({ ...d, columns: d.columns.map((x, j) => (j === i ? v : x)) })} />
              ))}
              <RowBtns onDel={() => setD({ ...d, columns: d.columns.slice(0, -1), rows: d.rows.map((r) => r.slice(0, -1)) })} onAdd={() => setD({ ...d, columns: [...d.columns, `col ${d.columns.length + 1}`], rows: d.rows.map((r) => [...r, ""]) })} />
            </div>
            <Lbl>rows</Lbl>
            {d.rows.map((row, ri) => (
              <div className="gb-ed-row" key={ri}>
                {row.map((cell, ci) => (
                  <EdInput key={ci} value={cell} placeholder="cell" className="gb-ed-sm" onChange={(v) => setD({ ...d, rows: d.rows.map((r, j) => (j === ri ? r.map((c, k) => (k === ci ? v : c)) : r)) })} />
                ))}
                <RowBtns onDel={() => setD({ ...d, rows: d.rows.filter((_, j) => j !== ri) })} onAdd={() => setD({ ...d, rows: [...d.rows.slice(0, ri + 1), d.columns.map(() => ""), ...d.rows.slice(ri + 1)] })} />
              </div>
            ))}
          </>
        );
      case "stat":
        return (
          <>
            {d.items.map((s, i) => (
              <div className="gb-ed-row" key={i}>
                <EdInput value={s.value} placeholder="value" className="gb-ed-xs" onChange={(v) => setD({ ...d, items: d.items.map((x, j) => (j === i ? { ...x, value: v } : x)) })} />
                <EdInput value={s.label} placeholder="label" className="gb-ed-sm" onChange={(v) => setD({ ...d, items: d.items.map((x, j) => (j === i ? { ...x, label: v } : x)) })} />
                <EdInput value={s.note ?? ""} placeholder="note (optional)" onChange={(v) => setD({ ...d, items: d.items.map((x, j) => (j === i ? { ...x, note: v } : x)) })} />
                <RowBtns onDel={() => setD({ ...d, items: d.items.filter((_, j) => j !== i) })} onAdd={() => setD({ ...d, items: [...d.items.slice(0, i + 1), { label: "", value: "" }, ...d.items.slice(i + 1)] })} />
              </div>
            ))}
          </>
        );
      case "diagram":
        return (
          <>
            <EdTitle value={d.title} onChange={(v) => setD({ ...d, title: v })} />
            <EdInput value={d.source} placeholder="diagram source (compact DSL or Mermaid)" className="gb-mono" onChange={(v) => setD({ ...d, source: v })} />
          </>
        );
      case "entities":
        return (
          <>
            <EdTitle value={d.title} onChange={(v) => setD({ ...d, title: v })} />
            <Lbl>one urn per line</Lbl>
            <EdInput value={d.ids.join("\n")} placeholder="urn:egae:…" className="gb-mono" onChange={(v) => setD({ ...d, ids: v.split("\n").map((s) => s.trim()).filter(Boolean) })} />
          </>
        );
      case "actions":
        return (
          <>
            <EdTitle value={d.title} onChange={(v) => setD({ ...d, title: v })} />
            <EdInput value={d.prompt ?? ""} placeholder="prompt (optional)" className="gb-inp-prose" onChange={(v) => setD({ ...d, prompt: v })} />
            <Lbl>options</Lbl>
            {d.options.map((o, i) => (
              <div className="gb-ed-row" key={i}>
                <EdInput value={o.label} placeholder="label" className="gb-ed-sm" onChange={(v) => setD({ ...d, options: d.options.map((x, j) => (j === i ? { ...x, label: v } : x)) })} />
                <EdInput value={o.send} placeholder="send to assistant" onChange={(v) => setD({ ...d, options: d.options.map((x, j) => (j === i ? { ...x, send: v } : x)) })} />
                <RowBtns onDel={() => setD({ ...d, options: d.options.filter((_, j) => j !== i) })} onAdd={() => setD({ ...d, options: [...d.options.slice(0, i + 1), { label: "", send: "" }, ...d.options.slice(i + 1)] })} />
              </div>
            ))}
          </>
        );
      case "group":
        return (
          <>
            <EdTitle value={d.title} onChange={(v) => setD({ ...d, title: v })} />
            <div className="gb-ed-row">
              <select className="field-input gb-ed-sm" value={d.tone ?? "framed"} onChange={(e) => setD({ ...d, tone: e.target.value as "plain" | "framed" | "accent" })}>
                {(["plain", "framed", "accent"] as const).map((tn) => <option key={tn} value={tn}>{tn}</option>)}
              </select>
              <select className="field-input gb-ed-sm" value={d.layout ?? "stack"} onChange={(e) => setD({ ...d, layout: e.target.value as "stack" | "row" | "grid" })}>
                {(["stack", "row", "grid"] as const).map((ly) => <option key={ly} value={ly}>{ly}</option>)}
              </select>
              <span className="gb-ed-lbl">{d.children.length} block(s) — edit them inside the group</span>
            </div>
          </>
        );
      case "business-canvas":
        // Reached only defensively (the ✎ toggle is hidden for this kind); edits inline.
        return <BusinessCanvas block={d} onChange={(b) => setD(b)} />;
    }
  })();

  return (
    <div className="gb-editor">
      {editor}
      <div className="gb-editor-actions">
        <button className="btn primary sm" onClick={() => onSave(d)}>save</button>
        <button className="btn ghost sm" onClick={onCancel}>cancel</button>
      </div>
    </div>
  );
}

// ── The recursive node ─────────────────────────────────────────────────────────────
/** Build the handlers for child index `i` of a group, rebuilding the group on change. */
function childHandlers(group: GroupData, i: number, onChange: (d: BlockData) => void, onAction: (s: string) => void): NodeHandlers {
  const setChildren = (children: readonly BlockData[]): void => onChange({ ...group, children });
  const swap = (j: number): void => {
    const next = [...group.children];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setChildren(next);
  };
  return {
    onChange: (cd) => setChildren(group.children.map((c, j) => (j === i ? cd : c))),
    onRemove: () => setChildren(group.children.filter((_, j) => j !== i)),
    onAction,
    ...(i > 0 ? { onMoveUp: () => swap(i - 1) } : {}),
    ...(i < group.children.length - 1 ? { onMoveDown: () => swap(i + 1) } : {}),
  };
}

function BlockNode({ ws, data, handlers, author, vtName, readOnly }: { ws: Workspace; data: BlockData; handlers: NodeHandlers; author?: BlockAuthor; vtName?: string; readOnly?: boolean | undefined }): JSX.Element {
  const [editing, setEditing] = useState(false);
  const isGroup = data.kind === "group";
  // The business-canvas edits itself inline (its own typed fields), so it skips the
  // ✎ → BlockEditor round-trip every other block uses.
  const isBusiness = data.kind === "business-canvas";

  return (
    <div className={`gb ${author ?? ""} ${isGroup ? "is-group" : ""} ${editing ? "editing" : ""} ${readOnly ? "readonly" : ""}`} style={vtName ? { viewTransitionName: vtName } : undefined}>
      {readOnly ? null : (
        <div className="gb-toolbar">
          {author ? <span className="gb-author" title={`authored by ${author}`}>{author === "agent" ? "✦" : "✎"}</span> : null}
          {handlers.onMoveUp ? <button className="gb-tb" onClick={handlers.onMoveUp} aria-label="move up">↑</button> : null}
          {handlers.onMoveDown ? <button className="gb-tb" onClick={handlers.onMoveDown} aria-label="move down">↓</button> : null}
          {isBusiness ? null : <button className="gb-tb" onClick={() => setEditing((v) => !v)} aria-label="edit">✎</button>}
          {handlers.onRemove ? <button className="gb-tb danger" onClick={handlers.onRemove} aria-label="remove">✕</button> : null}
        </div>
      )}

      {editing ? (
        <BlockEditor data={data} onSave={(d) => { handlers.onChange(d); setEditing(false); }} onCancel={() => setEditing(false)} />
      ) : isGroup ? (
        (() => {
          const g = data as GroupData;
          return (
            <section className={`gb-group tone-${g.tone ?? "framed"}`}>
              {g.title ? <div className="gb-group-title">{g.title}</div> : null}
              <div className={`gb-group-body layout-${g.layout ?? "stack"}`}>
                {g.children.map((child, i) => (
                  <BlockNode key={i} ws={ws} data={child} handlers={childHandlers(g, i, handlers.onChange, handlers.onAction)} readOnly={readOnly} />
                ))}
              </div>
            </section>
          );
        })()
      ) : (
        <Body ws={ws} data={data as LeafBlock} onUpdate={handlers.onChange} onAction={handlers.onAction} readOnly={readOnly} />
      )}
    </div>
  );
}

const NOOP_HANDLERS: NodeHandlers = { onChange: () => {}, onAction: () => {} };

/**
 * Read-only render of a list of blocks — the SAME renderer the scratchpad uses, with
 * the co-edit toolbar and interactions suppressed. Used to show a question's
 * consumption-only context (a diagram, a definition list) inside a primitive card, so
 * markdown and diagrams render identically in primitives and on the scratchpad.
 */
export function StaticBlocks({ ws, blocks }: { ws: Workspace; blocks: readonly BlockData[] }): JSX.Element {
  return (
    <div className="gb-static">
      {blocks.map((b, i) => (
        <BlockNode key={i} ws={ws} data={b} handlers={NOOP_HANDLERS} readOnly />
      ))}
    </div>
  );
}

/** The block kinds a participant can add to a canvas (group + every leaf). */
const ADDABLE: { kind: BlockData["kind"]; label: string; make: () => BlockData }[] = [
  { kind: "heading", label: "heading", make: () => ({ kind: "heading", text: "Section", level: 2 }) },
  { kind: "prose", label: "text", make: () => ({ kind: "prose", text: "" }) },
  { kind: "fields", label: "fields", make: () => ({ kind: "fields", rows: [{ label: "", value: "" }] }) },
  { kind: "list", label: "list", make: () => ({ kind: "list", items: [{ text: "" }] }) },
  { kind: "table", label: "table", make: () => ({ kind: "table", columns: ["", ""], rows: [["", ""]] }) },
  { kind: "diagram", label: "diagram", make: () => ({ kind: "diagram", source: "A -> B" }) },
  { kind: "callout", label: "callout", make: () => ({ kind: "callout", tone: "info", body: "" }) },
  { kind: "group", label: "group", make: () => ({ kind: "group", title: "Section", tone: "framed", children: [] }) },
  {
    kind: "business-canvas",
    label: "business canvas",
    make: (): BusinessCanvasBlock => ({
      kind: "business-canvas",
      view: "value-proposition",
      model: {
        segments: [{ id: "seg-1", name: "", tone: 0, jobs: [], pains: [], gains: [] }],
        valuePropositions: [{ id: "vp-1", name: "", segmentIds: ["seg-1"], products: [], painRelievers: [], gainCreators: [] }],
        channels: [], customerRelationships: [], revenueStreams: [], keyResources: [], keyActivities: [], keyPartnerships: [], costStructure: [],
      },
    }),
  },
];

/**
 * A local, editable canvas of GenUI blocks — the editing surface of the
 * generative-canvas primitive. It reuses {@link BlockNode}'s full co-edit affordances
 * (move / edit / remove, recursive groups) over a plain in-memory array, so a
 * participant shapes a structured artifact (e.g. a Value Proposition Canvas) the same
 * way the assistant co-edits the scratchpad. State lives in the parent (controlled).
 */
export function CanvasEditor({ ws, blocks, onChange }: { ws: Workspace; blocks: readonly BlockData[]; onChange: (blocks: readonly BlockData[]) => void }): JSX.Element {
  const swap = (i: number, j: number): void => {
    const next = [...blocks];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  return (
    <div className="canvas-edit">
      {blocks.map((b, i) => {
        const handlers: NodeHandlers = {
          onChange: (d) => onChange(blocks.map((x, j) => (j === i ? d : x))),
          onAction: () => {},
          onRemove: () => onChange(blocks.filter((_, j) => j !== i)),
          ...(i > 0 ? { onMoveUp: () => swap(i, i - 1) } : {}),
          ...(i < blocks.length - 1 ? { onMoveDown: () => swap(i, i + 1) } : {}),
        };
        return <BlockNode key={i} ws={ws} data={b} handlers={handlers} />;
      })}
      <div className="canvas-add">
        <span className="gb-ed-lbl">add block</span>
        {ADDABLE.map((a) => (
          <button key={a.kind} type="button" className="btn ghost sm" onClick={() => onChange([...blocks, a.make()])}>
            ＋ {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function BlockView({
  ws,
  block,
  index,
  count,
  handlers,
}: {
  ws: Workspace;
  block: ScratchpadBlock;
  index: number;
  count: number;
  handlers: BlockHandlers;
}): JSX.Element {
  const nodeHandlers: NodeHandlers = {
    onChange: (d) => handlers.onUpdate(block.id, d),
    onAction: handlers.onAction,
    onRemove: () => handlers.onRemove(block.id),
    ...(index > 0 ? { onMoveUp: () => handlers.onMove(block.id, -1) } : {}),
    ...(index < count - 1 ? { onMoveDown: () => handlers.onMove(block.id, 1) } : {}),
  };
  return <BlockNode ws={ws} data={block.data} handlers={nodeHandlers} author={block.author} vtName={`genui-${block.id}`} />;
}
